import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Layers3, Users, Sparkles, BookOpen, FolderOpen, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CommunityCardGroup from "../components/CommunityCardGroup";
import Leaderboard from "../components/Leaderboard";

export default function Community() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const cacheRef = useRef(new Map());
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/community/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    const cached = cacheRef.current.get(q.toLowerCase());
    if (cached) {
      setResults(cached);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const myId = ++reqIdRef.current;
      setSearching(true);
      try {
        const res = await api.get("/community/search", { params: { q } });
        cacheRef.current.set(q.toLowerCase(), res.data);
        if (myId === reqIdRef.current) setResults(res.data);
      } catch {
        if (myId === reqIdRef.current) setResults(null);
      } finally {
        if (myId === reqIdRef.current) setSearching(false);
      }
    }, 150);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h1 data-testid="community-title" className="font-display text-4xl sm:text-5xl font-bold">Explore</h1>
          <button
            data-testid="community-share-button"
            onClick={async () => {
              const url = `${window.location.origin}/api/community/share`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Explore link copied — paste it anywhere");
              } catch {
                toast.error("Copy failed — link: " + url);
              }
            }}
            className="inline-flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full border border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
        <p className="text-zinc-400 text-sm md:text-base mb-6">
          Every card scanned into CardArk — search cards, sets, collections and collectors' binders.
        </p>

        {stats && (
          <div data-testid="community-stats" className="flex flex-wrap gap-3 mb-8">
            <StatChip icon={Layers3} label="cards catalogued" value={stats.total_cards} />
            <StatChip icon={Sparkles} label="unique cards" value={stats.unique_cards} />
            <StatChip icon={Users} label="collectors" value={stats.collectors} />
          </div>
        )}

        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            data-testid="community-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a card, set, collection or binder name..."
            className="w-full bg-[#111113] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]/50"
          />
        </div>

        {searching && <p className="text-sm text-zinc-500" data-testid="community-searching">Searching...</p>}

        {!results && !searching && <Leaderboard />}

        {results && (
          <div className="space-y-10">
            {results.binders.length > 0 && (
              <section data-testid="community-binder-results">
                <h2 className="text-base md:text-lg font-semibold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#A78BFA]" /> Binders</h2>
                <div className="flex flex-wrap gap-3">
                  {results.binders.map((b) => (
                    <Link
                      key={b.user_id}
                      to={`/collector/${b.user_id}`}
                      data-testid={`community-binder-${b.user_id}`}
                      className="flex items-center gap-2 border border-white/10 bg-[#111113] hover:border-[#A78BFA]/40 rounded-full px-4 py-2 text-sm transition-colors"
                    >
                      <span className="font-semibold">{b.binder_name}</span>
                      <span className="text-xs text-zinc-500">{b.card_count} cards</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {results.collections.length > 0 && (
              <section data-testid="community-collection-results">
                <h2 className="text-base md:text-lg font-semibold mb-3 flex items-center gap-2"><FolderOpen className="w-4 h-4 text-[#A78BFA]" /> Collections</h2>
                <div className="flex flex-wrap gap-3">
                  {results.collections.map((c) => (
                    <Link
                      key={c.collection_id}
                      to={`/collector/${c.user_id}?collection=${c.collection_id}`}
                      data-testid={`community-collection-${c.collection_id}`}
                      className="flex items-center gap-2 border border-white/10 bg-[#111113] hover:border-[#A78BFA]/40 rounded-full px-4 py-2 text-sm transition-colors"
                    >
                      <span className="font-semibold">{c.name}</span>
                      <span className="text-xs text-zinc-500">{c.owner_name} · {c.card_count} cards</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section data-testid="community-card-results">
              <h2 className="text-base md:text-lg font-semibold mb-3 flex items-center gap-2"><Layers3 className="w-4 h-4 text-[#A78BFA]" /> Cards</h2>
              {results.cards.length === 0 ? (
                <p className="text-sm text-zinc-600">No cards match that search yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.cards.map((group, i) => (
                    <CommunityCardGroup key={`${group.name}-${group.set_name}-${group.card_number}-${i}`} group={group} loggedIn={!!user} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <span className="flex items-center gap-2 border border-white/10 bg-[#111113] rounded-full px-4 py-2 text-sm">
      <Icon className="w-4 h-4 text-[#A78BFA]" />
      <span className="font-bold">{value?.toLocaleString?.() ?? value}</span>
      <span className="text-xs text-zinc-500">{label}</span>
    </span>
  );
}
